interface FormHandlerOptions {
	formId: string;
	turnstileSelector?: string;
}

export class FormHandler {
	private form: HTMLFormElement;
	private messageContainer: HTMLDivElement;
	private turnstileWidgetId: string | null = null;
	private turnstileSelector: string;

	constructor({ formId, turnstileSelector = '.cf-turnstile' }: FormHandlerOptions) {
		const form = document.getElementById(formId) as HTMLFormElement;
		if (!form) {
			throw new Error(`Form with ID ${formId} not found`);
		}

		this.form = form;
		this.messageContainer = form.querySelector('.form-messages') as HTMLDivElement;
		this.turnstileSelector = turnstileSelector;
		if (null !== this.form.querySelector(this.turnstileSelector)) {
			this.init();
		}
	}

	private setMessage(message: string, isSuccess = false) {
		if (this.messageContainer) {
			// Clear any existing messages
			this.messageContainer.innerHTML = '';

			// Create new message element
			const messageElement = document.createElement('p');
			messageElement.className = isSuccess
				? 'form-messages__success'
				: 'form-messages__error';
			messageElement.textContent = message;

			this.messageContainer.appendChild(messageElement);
		}
	}

	private async initializeTurnstile(): Promise<void> {
		const turnstileContainer = this.form.querySelector<HTMLElement>(this.turnstileSelector);
		const turnstileSitekey = turnstileContainer?.dataset.sitekey;

		if (!turnstileContainer || !turnstileSitekey) {
			this.setMessage('Turnstile configuration is missing.');
			throw new Error('Turnstile configuration is missing');
		}

		return new Promise<void>((resolve) => {
			window.turnstile.ready(() => {
				this.turnstileWidgetId = window.turnstile.render(turnstileContainer, {
					sitekey: turnstileSitekey,
					callback: () => {
						resolve();
					},
					'expired-callback': () => {
						this.setMessage('Captcha token expired. Please try again.');
						window.turnstile.reset(this.turnstileWidgetId);
					},
					'timeout-callback': () => {
						this.setMessage('Captcha verification timed out. Please try again.');
						window.turnstile.reset(this.turnstileWidgetId);
					},
				});
			});
		});
	}

	private async verifyTurnstile(turnstileResponse: string): Promise<boolean> {
		const verifyResponse = await fetch('/wp-json/sageverse/v1/verify-turnstile', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				'cf-turnstile-response': turnstileResponse,
			}),
		});

		const verifyData = await verifyResponse.json();

		if (!verifyData.success) {
			this.setMessage(verifyData.message || 'Captcha verification failed. Please try again.');
			window.turnstile.reset(this.turnstileWidgetId);
			return false;
		}

		return true;
	}

	private async handleSubmit(e: SubmitEvent) {
		e.preventDefault();

		try {
			// Initialize turnstile if not already done
			if (!this.turnstileWidgetId) {
				await this.initializeTurnstile();
			}

			// Get the turnstile token
			const turnstileResponse = window.turnstile?.getResponse(this.turnstileWidgetId);
			if (!turnstileResponse) {
				this.setMessage('Please complete the captcha verification.');
				return;
			}

			// Verify turnstile
			const isVerified = await this.verifyTurnstile(turnstileResponse);
			if (!isVerified) return;

			// If verification successful, submit the form
			this.form.submit();
		} catch (error) {
			this.setMessage('An error occurred. Please try again.');
			if (this.turnstileWidgetId) {
				window.turnstile.reset(this.turnstileWidgetId);
			}
		}
	}

	private init() {
		this.form.addEventListener('submit', (e) => this.handleSubmit(e));
	}
}
